import React, { useState, useEffect, useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import teacherService from '../../services/api/teacher';
import { getImageUrl } from '../../config/apiConfig';

import { useQuery } from 'react-query';

import { getArabicDate } from '../../services/utils/date';

import LoadingSpinner from '../../components/common/LoadingSpinner';

import { useStudentsSearch } from '../../hooks/useSearch';

import { 

  Users, 

  Search, 

  Calendar, 

  UserCheck, 

  Mail, 

  Filter, 

  ChevronLeft, 

  ShieldCheck,

  UserPlus,

  MoreVertical,

  Sparkles,

  MapPin,

  ChevronDown

} from 'lucide-react';



const TeacherStudents = () => {

  const navigate = useNavigate();

  const [selectedSubject, setSelectedSubject] = useState('');

  const [selectedStage, setSelectedStage] = useState('');

  const [selectedGrade, setSelectedGrade] = useState('');

  const [showFilters, setShowFilters] = useState(false);

  // جلب بيانات الفلاتر الخاصة بالمدرس

  const { data: teacherSubjectsData } = useQuery(
    ['teacher-subjects', 'filters'],
    () => teacherService.getSubjects(),
    {
      retry: false,
      staleTime: 300000,
      refetchOnWindowFocus: false
    }
  );

  // Extract unique stages and grades from teacher's subjects

  const filters = useMemo(() => {

    // Check if teacherSubjectsData is directly an array or has subjects property

    const subjectsArray = Array.isArray(teacherSubjectsData) ? teacherSubjectsData : teacherSubjectsData?.subjects || [];

    if (!subjectsArray || subjectsArray.length === 0) {

      return { stages: [], subjects: [], grades: [] };

    }

    const stagesSet = new Set();

    const gradesSet = new Set();

    const stages = [];

    const grades = [];

    subjectsArray.forEach(subject => {

      // Add stage if not already added - use stage_name as unique identifier

      if (subject.stage_name && !stagesSet.has(subject.stage_name)) {

        stagesSet.add(subject.stage_name);

        stages.push({

          id: subject.stage_name, // Use stage_name as ID for consistency

          name: subject.stage_name

        });

      }

      // Add grade if not already added

      if (subject.grade_id && !gradesSet.has(subject.grade_id)) {

        gradesSet.add(subject.grade_id);

        grades.push({

          id: subject.grade_id,

          name: subject.grade_name,

          stage_name: subject.stage_name // Use stage_name instead of stage_id

        });

      }

    });

    return {

      stages: stages.sort((a, b) => a.name.localeCompare(b.name)),

      subjects: subjectsArray.map(s => ({

        id: s.teacher_subject_id,

        name: s.subject_name,

        grade_name: s.grade_name,

        stage_name: s.stage_name // Add stage_name for filtering

      })).sort((a, b) => a.name.localeCompare(b.name)),

      grades: grades.sort((a, b) => a.name.localeCompare(b.name))

    };

  }, [teacherSubjectsData]);

  // Extract grades based on selected stage

  const allGrades = useMemo(() => {

    if (!selectedStage) return filters?.grades || [];

    return filters?.grades?.filter(grade => grade.stage_name === selectedStage) || [];

  }, [selectedStage, filters?.grades]);

  // جلب قائمة الطلاب بدون فلترة (سنقوم بالفلترة محلياً)

  const { data: studentsData, isLoading } = useQuery(

    'teacher-students',

    () => teacherService.getStudents(),

    {

      retry: false,

      staleTime: 30000,

      refetchOnWindowFocus: false

    }

  );

  const students = studentsData?.students || [];

  // استخدام الـ Hook الجديد للبحث والفلترة

  const {

    searchTerm: hookSearchTerm,

    filteredData: searchedStudents,

    setSearchTerm: setHookSearchTerm,

    updateFilter

  } = useStudentsSearch(students);

  // Handle search input change

  const handleSearch = (e) => {

    setHookSearchTerm(e.target.value);

  };

  // Apply filters when they change

  useEffect(() => {

    // Apply subject filter

    if (selectedSubject) {

      updateFilter('teacher_subject_id', parseInt(selectedSubject));

    } else {

      updateFilter('teacher_subject_id', '');

    }

    // Apply stage filter

    if (selectedStage) {

      updateFilter('stage_name', selectedStage);

    } else {

      updateFilter('stage_name', '');

    }

    // Apply grade filter

    if (selectedGrade) {

      updateFilter('grade_id', parseInt(selectedGrade));

    } else {

      updateFilter('grade_id', '');

    }

  }, [selectedSubject, selectedStage, selectedGrade, updateFilter]);



  if (isLoading) {

    return (

      <div className="flex flex-col items-center justify-center py-24 min-h-[500px]">

        <LoadingSpinner size="large" />

        <p className="mt-4 text-gray-500 font-bold animate-pulse uppercase tracking-widest text-xs">جاري جلب قائمة طلابك...</p>

      </div>

    );

  }



  return (

    <div className="max-w-7xl mx-auto space-y-10 px-4 sm:px-6 py-6 min-h-screen bg-gray-50/30">

      

      {/* Header Section */}

      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-10 relative overflow-hidden">

        <div className="absolute top-0 left-0 w-32 h-32 bg-primary-50 rounded-br-[5rem] opacity-40"></div>

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">

          <div className="space-y-1">

            <h1 className="text-3xl lg:text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">

                <Users className="text-primary-600" size={32} />

                إدارة الطلاب

            </h1>

            <p className="text-gray-500 font-medium flex items-center gap-2">

                <ShieldCheck size={18} className="text-emerald-500" />

                قائمة الطلاب المشتركين حالياً في موادك الدراسية

            </p>

          </div>

          <div className="bg-primary-50 p-6 rounded-[2rem] border border-primary-100 flex items-center gap-5 group hover:shadow-md transition-all">

            <div className="w-12 h-12 bg-primary-600 text-white rounded-2xl flex items-center justify-center shadow-lg shadow-primary-200 group-hover:scale-110 transition-transform">

                <UserCheck size={24} />

            </div>

            <div>

                <div className="text-[10px] font-black text-primary-700 uppercase tracking-widest">إجمالي الطلاب</div>
                <p className="text-3xl font-black leading-none">{searchedStudents?.length || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Filters Bar */}
      <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-6 overflow-visible relative z-40">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
          
          {/* Main Search Input */}
          <div className="relative md:col-span-8 group">
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500 transition-colors w-5 h-5" />
            <input
              type="text"
              placeholder="ابحث عن طالب بالاسم أو البريد الإلكتروني..."
              value={hookSearchTerm}
              onChange={handleSearch}
              className="w-full pr-12 pl-4 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-medium"
            />
          </div>
          
          {/* Advanced Filters Button */}
          <div className="md:col-span-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`w-full flex items-center justify-center gap-2 py-4 border rounded-2xl font-bold transition-all ${showFilters ? 'bg-primary-50 border-primary-200 text-primary-600' : 'bg-white border-gray-100 text-gray-500 hover:bg-gray-50'}`}
            >
              <Filter size={18} />
              الفلاتر
              <ChevronDown size={18} className={`transition-transform duration-300 ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          <div className="md:col-span-2">
            <div className="bg-primary-600 text-white py-4 rounded-2xl text-center font-black text-xs uppercase tracking-widest shadow-lg shadow-primary-100">
                {searchedStudents?.length || 0} طالب مـسـجـل
            </div>
          </div>

        </div>

        {/* Expandable Advanced Filters */}
        {showFilters && (
          <div className="mt-6 pt-6 border-t border-gray-50 grid grid-cols-1 md:grid-cols-3 gap-4
                          animate-in fade-in slide-in-from-top duration-300
                          relative z-50 overflow-visible">
            
            {/* المرحلة الدراسية */}
            <div className="space-y-2 relative overflow-visible">
                <label className="text-[10px] font-black text-gray-400 uppercase mr-2 tracking-widest">المرحلة الدراسية</label>
                <div className="relative overflow-visible">
                    <select
                    value={selectedStage}
                    onChange={(e) => {
                      setSelectedStage(e.target.value);
                      setSelectedGrade(''); // Reset grade when stage changes
                    }}
                    className="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:bg-white transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                    >
                    <option value="">جميع المراحل</option>
                    {filters?.stages?.map((stage) => (
                        <option key={stage.id} value={stage.id}>{stage.name}</option>
                    ))}
                    </select>
                </div>
            </div>

            {/* جميع المواد */}
            <div className="space-y-2 relative overflow-visible">
                <label className="text-[10px] font-black text-gray-400 uppercase mr-2 tracking-widest">جميع المواد</label>
                <div className="relative overflow-visible">
                    <select
                    value={selectedSubject}
                    onChange={(e) => setSelectedSubject(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:bg-white transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                    >
                    <option value="">جميع المواد</option>
                    {filters?.subjects?.map((subject) => (
                        <option key={subject.id} value={subject.id}>
                          {subject.name} - {subject.grade_name}
                        </option>
                    ))}
                    </select>
                </div>
            </div>

            {/* الصف الدراسي */}
            <div className="space-y-2 relative overflow-visible">
                <label className="text-[10px] font-black text-gray-400 uppercase mr-2 tracking-widest">الصف الدراسي</label>
                <div className="relative overflow-visible">
                    <select
                    value={selectedGrade}
                    onChange={(e) => setSelectedGrade(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:bg-white transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                    >
                    <option value="">جميع الصفوف</option>
                    {allGrades?.map((grade) => (
                        <option key={grade.id} value={grade.id}>{grade.name}</option>
                    ))}
                    </select>
                </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content Area */}

      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden group transition-all duration-500 hover:shadow-xl">
        {!searchedStudents || searchedStudents?.length === 0 ? (
          <div className="py-24 text-center">
            <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <UserPlus size={48} className="text-gray-200" />
            </div>
            <h3 className="text-2xl font-black text-gray-900 mb-2">{hookSearchTerm ? 'لا توجد نتائج لبحثك' : 'لا يوجد طلاب مطابقين للبحث'}</h3>
            <p className="text-gray-500 max-w-sm mx-auto font-medium">
              {hookSearchTerm ? 'جرب البحث بكلمات أخرى أو قم بإعادة ضبط الفلاتر.' : 'لم يشترك أي طلاب في هذا القسم بعد، أو حاول تغيير كلمات البحث المستخدمة.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-50">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">بيانات الطالب</th>
                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">المحتوى المشترك فيه</th>
                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">العنوان</th>
                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">تواريخ الاشتراك</th>
                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">حالة الحساب</th>
                  <th className="px-8 py-6 text-left text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">الإجراءات</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-50">
                {Array.isArray(searchedStudents) && searchedStudents.map((student, index) => (
                  <tr key={`${student.id}-${student.subject_name || ''}-${index}`} className="hover:bg-primary-50/30 transition-colors group/row">
                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex items-center gap-4">

                        <div className="flex-shrink-0 relative">

                          {student.avatar ? (
                            <img
                              src={getImageUrl(student.avatar)}
                              alt={student.name}
                              className="h-12 w-12 rounded-2xl object-cover shadow-lg group-hover/row:scale-110 transition-transform"
                              onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-black shadow-lg shadow-primary-100 group-hover/row:scale-110 transition-transform">
                              {student.name?.charAt(0).toUpperCase()}
                            </div>
                          )}

                          <div className="absolute -bottom-1 -left-1 bg-green-500 w-4 h-4 rounded-full border-4 border-white"></div>

                        </div>

                        <div>

                          <div className="text-base font-black text-gray-900 group-hover/row:text-primary-600 transition-colors">{student.name}</div>

                          <div className="flex items-center gap-1.5 text-xs font-bold text-gray-400 mt-0.5 tracking-tighter">

                             <Mail size={12} className="text-primary-400" /> {student.email}

                          </div>

                        </div>

                      </div>

                    </td>

                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="bg-primary-50 text-primary-600 px-2.5 py-1 rounded-lg text-[10px] font-black border border-primary-100 uppercase tracking-widest">
                          {student.title || student.subject_name}
                        </span>
                      </div>
                    </td>

                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="text-[10px] font-bold text-gray-400 flex items-center gap-1">
                        <MapPin size={14} className="text-primary-400" />
                        <span className="text-sm font-black text-gray-700">{student.address || 'غير متوفر'}</span>
                      </div>
                    </td>

                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2 text-sm font-black text-gray-700">
                          <Calendar className="w-4 h-4 text-primary-500" />
                          <span>{getArabicDate(student.created_at)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm font-black text-gray-700">
                          <Calendar className="w-4 h-4 text-orange-500" />
                          <span>{student.end_date ? getArabicDate(student.end_date) : 'غير محدد'}</span>
                        </div>
                        <div className="text-[10px] font-bold text-gray-400 mt-1 uppercase tracking-widest mr-6">تاريخ الاشتراك / الانتهاء</div>
                      </div>
                    </td>

                    <td className="px-8 py-6 whitespace-nowrap">

                      <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-[10px] font-black bg-emerald-50 text-emerald-600 border border-emerald-100 shadow-sm uppercase tracking-widest">

                        <CheckCircle2 size={12} /> اشتراك نشط

                      </span>

                    </td>

                    <td className="px-8 py-6 whitespace-nowrap text-left">

                        <div className="flex items-center justify-end gap-2 opacity-0 group-hover/row:opacity-100 transition-opacity">

                            <button 

                                onClick={() => navigate(`/student/profile/${student.id}`)}

                                className="bg-white border border-gray-200 text-gray-600 px-5 py-2.5 rounded-xl font-black text-xs hover:bg-primary-600 hover:text-white hover:border-primary-600 transition-all shadow-sm"

                            >

                                عرض الملف

                            </button>

                            <button className="p-2.5 text-gray-400 hover:text-gray-900 transition-colors">

                                <MoreVertical size={20} />

                            </button>

                        </div>

                    </td>

                  </tr>

                ))}

              </tbody>

            </table>

          </div>

        )}

      </div>



      {/* Footer Info Card */}

      <div className="bg-gray-900 rounded-[2.5rem] p-10 text-white flex flex-col md:flex-row items-center justify-between gap-8 relative overflow-hidden group shadow-2xl">

        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-600/20 rounded-full blur-3xl -z-0"></div>

        <div className="relative z-10 text-center md:text-right space-y-2">

            <h3 className="text-2xl font-black flex items-center gap-2 justify-center md:justify-start leading-tight">

                <Sparkles className="text-primary-500" /> نصيحة لتطوير التفاعل

            </h3>

            <p className="text-gray-400 font-medium max-w-xl">الطلاب الذين يتلقون ردوداً سريعة على أسئلتهم هم الأكثر ميلاً لتجديد اشتراكاتهم. تابع قسم "الأسئلة" دورياً.</p>

        </div>

        <button 

            onClick={() => navigate('/teacher/questions')}

            className="relative z-10 bg-white text-gray-900 px-10 py-4 rounded-2xl font-black flex items-center gap-2 shadow-xl hover:bg-primary-50 transition-all transform active:scale-95"

        >

            غرفة الأسئلة <ChevronLeft size={20} />

        </button>

      </div>

    </div>

  );

};



// أيقونات إضافية مستخدمة في التصميم

const CheckCircle2 = ({ size, className }) => (

    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={className}>

        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />

        <polyline points="22 4 12 14.01 9 11.01" />

    </svg>

);



export default TeacherStudents;